import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

// Matches the row's `columnGap`, so the measurement and the layout cannot disagree.
export const FIELD_GAP_PX = 32;

export type BandFieldSpec = {
    // Identifies the field for React, since labels are not guaranteed unique.
    fieldKey: string;
    label: string;
    value: ReactNode;
    canWrap?: boolean;
};

type FieldWidths = { oneLine: number; wrapped: number };

type FieldLayout = { visibleCount: number; wrapped: boolean };

const widthAt = (field: HTMLElement, width: 'max-content' | 'min-content'): number => {
    const previous = field.style.width;
    field.style.width = width;
    const measured = field.getBoundingClientRect().width;
    field.style.width = previous;
    return measured;
};

const measureField = (field: HTMLElement, canWrap: boolean | undefined): FieldWidths => {
    if (!canWrap) {
        const { width } = field.getBoundingClientRect();
        return { oneLine: width, wrapped: width };
    }
    return { oneLine: widthAt(field, 'max-content'), wrapped: widthAt(field, 'min-content') };
};

const countFitting = (widths: FieldWidths[], available: number, wrapped: boolean): number => {
    let used = 0;
    let fits = 0;
    widths.forEach((width, index) => {
        if (fits !== index) {
            return;
        }
        const needed = (wrapped ? width.wrapped : width.oneLine) + (index > 0 ? FIELD_GAP_PX : 0);
        if (used + needed > available) {
            return;
        }
        used += needed;
        fits = index + 1;
    });
    return fits;
};

// How many fields the row can hold, measured rather than assumed.
// Each field's width is read once while they are all in the layout,
// then a resize decides how many of those widths fit the space the row was given.
// Fields that can wrap only do so when that lets more fields show.
export const useVisibleFieldCount = (fields: BandFieldSpec[]) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const fieldWidthsRef = useRef<FieldWidths[]>([]);
    const [layout, setLayout] = useState<FieldLayout>({
        visibleCount: fields.length,
        wrapped: false,
    });
    const measuredFieldsRef = useRef(fields);

    if (measuredFieldsRef.current !== fields) {
        measuredFieldsRef.current = fields;
        fieldWidthsRef.current = [];
        if (layout.visibleCount !== fields.length || layout.wrapped) {
            setLayout({ visibleCount: fields.length, wrapped: false });
        }
    }

    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row) {
            return undefined;
        }

        const measure = () => {
            if (fieldWidthsRef.current.length !== fields.length) {
                fieldWidthsRef.current = Array.from(
                    row.children as HTMLCollectionOf<HTMLElement>,
                ).map((field, index) => measureField(field, fields[index].canWrap));
            }

            const available = row.clientWidth - FIELD_GAP_PX;
            const oneLineCount = countFitting(fieldWidthsRef.current, available, false);
            const wrappedCount = countFitting(fieldWidthsRef.current, available, true);
            const next =
                wrappedCount > oneLineCount
                    ? { visibleCount: wrappedCount, wrapped: true }
                    : { visibleCount: oneLineCount, wrapped: false };

            setLayout((previous) =>
                previous.visibleCount === next.visibleCount && previous.wrapped === next.wrapped
                    ? previous
                    : next,
            );
        };

        const observer = new ResizeObserver(measure);
        observer.observe(row);
        measure();

        return () => observer.disconnect();
    }, [fields]);

    return { rowRef, ...layout };
};
